import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Search, Filter, CheckCircle, XCircle, Clock, DollarSign, User, Calendar } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import payoutService from '../../services/payoutService';
import toast from 'react-hot-toast';

const AdminPayoutsContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const PayoutsTable = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 120px 150px 150px 200px;
  gap: 20px;
  padding: 20px;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
  border-bottom: 1px solid #2d2d44;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const TableHeaderCell = styled.div`
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 80px 1fr 120px 150px 150px 200px;
  gap: 20px;
  padding: 20px;
  border-bottom: 1px solid #2d2d44;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(90deg, rgba(0, 212, 255, 0.05) 0%, transparent 100%);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const TableCell = styled.div`
  color: #ffffff;
  font-size: 14px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const RequestId = styled.span`
  color: #00d4ff;
  font-weight: 600;
`;

const PlayerName = styled.span`
  color: #ffffff;
  font-weight: 500;
`;

const Amount = styled.span`
  color: #4ecdc4;
  font-weight: 600;
`;

const ShabaNumber = styled.span`
  color: #b8b8d1;
  font-family: monospace;
  font-size: 12px;
`;

const RequestDate = styled.span`
  color: #6c6c8a;
  font-size: 12px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: ${props => {
    switch(props.variant) {
      case 'approve': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'reject': return 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)';
      case 'process': return 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #5a5a7a 100%)';
    }
  }};
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 80px;
  justify-content: center;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #b8b8d1;
`;

const RejectModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const RejectModalContent = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  padding: 25px;
`;

const RejectModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const RejectModalTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const RejectModalBody = styled.div`
  margin-bottom: 20px;
`;

const RejectTextArea = styled.textarea`
  width: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 12px 15px;
  color: #ffffff;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  
  &::placeholder {
    color: #6c6c8a;
  }
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
`;

const RejectModalFooter = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
`;

const AdminPayouts = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRequestId, setRejectRequestId] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [approvedRequests, setApprovedRequests] = useState({});

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await payoutService.getPendingWithdrawals();
      setRequests(Array.isArray(data) ? data : []);
      setApprovedRequests({});
    } catch (error) {
      console.error('Error fetching withdrawal requests:', error);
      toast.error('خطا در بارگذاری درخواست‌های برداشت');
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = requests.filter(request =>
    request.playerUsername?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.id?.toString().includes(searchTerm) ||
    request.shabaNumber?.includes(searchTerm)
  );

  const handleApprove = async (requestId) => {
    setActionLoading(requestId);
    try {
      await payoutService.approveWithdrawal(requestId);
      setApprovedRequests(prev => ({ ...prev, [requestId]: true }));
      toast.success('درخواست تایید شد');
    } catch (error) {
      toast.error('خطا در تایید درخواست');
      console.error('Error approving withdrawal:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = (requestId) => {
    setRejectRequestId(requestId);
    setRejectionNotes('');
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectionNotes.trim()) {
      toast.error('لطفاً دلیل رد درخواست را وارد کنید');
      return;
    }

    setActionLoading(rejectRequestId);
    try {
      await payoutService.rejectWithdrawal(rejectRequestId, rejectionNotes);
      toast.success('درخواست رد شد');
      setShowRejectModal(false);
      setRejectRequestId(null);
      setRejectionNotes('');
      fetchRequests();
    } catch (error) {
      toast.error('خطا در رد درخواست');
      console.error('Error rejecting withdrawal:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleProcessPayout = async (requestId) => {
    setActionLoading(requestId);
    try {
      await payoutService.processPayout(requestId);
      toast.success('پرداخت انجام شد');
      fetchRequests();
    } catch (error) {
      toast.error('خطا در پردازش پرداخت');
      console.error('Error processing payout:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setRejectRequestId(null);
    setRejectionNotes('');
  };

  if (loading) {
    return (
      <AdminPayoutsContainer>
        <LoadingSpinner>
          <div>در حال بارگذاری...</div>
        </LoadingSpinner>
      </AdminPayoutsContainer>
    );
  }

  return (
    <AdminPayoutsContainer>
      <Header>
        <Title>مدیریت پرداخت‌های ادمین</Title>
        <SearchContainer>
          <Input
            type="text"
            placeholder="جستجو در درخواست‌ها..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search size={16} />}
          />
          <Button variant="outline" icon={<Filter size={16} />}>
            فیلتر
          </Button>
        </SearchContainer>
      </Header>

      {filteredRequests.length === 0 ? (
        <EmptyState>
          <EmptyIcon>💰</EmptyIcon>
          <EmptyTitle>هیچ درخواست برداشتی یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'هیچ درخواستی با این جستجو یافت نشد' : 'در حال حاضر هیچ درخواست برداشتی در انتظار نیست'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <PayoutsTable>
          <TableHeader>
            <TableHeaderCell>شناسه</TableHeaderCell>
            <TableHeaderCell>بازیکن</TableHeaderCell>
            <TableHeaderCell>مبلغ</TableHeaderCell>
            <TableHeaderCell>شماره شبا</TableHeaderCell>
            <TableHeaderCell>تاریخ درخواست</TableHeaderCell>
            <TableHeaderCell>عملیات</TableHeaderCell>
          </TableHeader>
          
          {filteredRequests.map((request) => (
            <TableRow
              key={request.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <TableCell>
                <RequestId>#{request.id}</RequestId>
              </TableCell>
              <TableCell>
                <PlayerName>{request.playerUsername}</PlayerName>
              </TableCell>
              <TableCell>
                <Amount>{request.amount?.toLocaleString()} تومان</Amount>
              </TableCell>
              <TableCell>
                <ShabaNumber>{request.shabaNumber}</ShabaNumber>
              </TableCell>
              <TableCell>
                <RequestDate>
                  {new Date(request.requestDate).toLocaleDateString('fa-IR')}
                </RequestDate>
              </TableCell>
              <TableCell>
                <ActionButtons>
                  <ActionButton
                    variant="approve"
                    disabled={actionLoading === request.id || approvedRequests[request.id]}
                    onClick={() => handleApprove(request.id)}
                  >
                    <CheckCircle size={14} />
                    {actionLoading === request.id && !approvedRequests[request.id] ? 'در حال...' : 'تایید'}
                  </ActionButton>
                  <ActionButton
                    variant="reject"
                    disabled={actionLoading === request.id}
                    onClick={() => handleReject(request.id)}
                  >
                    <XCircle size={14} />
                    رد
                  </ActionButton>
                  <ActionButton
                    variant="process"
                    disabled={!approvedRequests[request.id] || actionLoading === request.id}
                    onClick={() => handleProcessPayout(request.id)}
                  >
                    <DollarSign size={14} />
                    {actionLoading === request.id && approvedRequests[request.id] ? 'در حال...' : 'پرداخت'}
                  </ActionButton>
                </ActionButtons>
              </TableCell>
            </TableRow>
          ))}
        </PayoutsTable>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <RejectModal onClick={handleCloseRejectModal}>
          <RejectModalContent onClick={(e) => e.stopPropagation()}>
            <RejectModalHeader>
              <RejectModalTitle>رد درخواست برداشت</RejectModalTitle>
              <Button variant="outline" onClick={handleCloseRejectModal} icon={<XCircle size={16} />}>
                بستن
              </Button>
            </RejectModalHeader>
            
            <form onSubmit={handleRejectSubmit}>
              <RejectModalBody>
                <RejectTextArea
                  placeholder="دلیل رد درخواست را وارد کنید..."
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  required
                />
              </RejectModalBody>
              
              <RejectModalFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCloseRejectModal}
                  disabled={actionLoading === rejectRequestId}
                >
                  انصراف
                </Button>
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={actionLoading === rejectRequestId || !rejectionNotes.trim()}
                  icon={<XCircle size={16} />}
                >
                  {actionLoading === rejectRequestId ? 'در حال...' : 'رد درخواست'}
                </Button>
              </RejectModalFooter>
            </form>
          </RejectModalContent>
        </RejectModal>
      )}
    </AdminPayoutsContainer>
  );
};

export default AdminPayouts;
